namespace Solovis.UI.InputControls {

    /*
    * TextAreaInputControl is a UI element for entering a large amount of text.
    * 
    * options:
    * width: width of the control
    * readonly: disallow user input but still allow value to post
    * max: max length for text.
    * min: min length for text.
    * placeholder: hint displayed when empty
    * required: whether or not the input is required.
    * value: initial value
    * rows: number of rows to display
    * cssRules: array of objects of type
    *   {
    *       rule,
    *       value
    *   }
    * display: whether or not to display the input control and/or validation messages
    * {
    *   showInput: true,
    *   showValidation: true
    * }
    * validate: custom validation function.  must return an object with the following two properties:
    * {
    *   isValid: true,
        message: null
    * }
    * 
    * change: custom change function
    * enterKey: callback on enter key keyup.  Will only fire if input is valid
    * escapeKey: callback on esc key keyup
    * tabKey: callback on tab key keydown.  Will only fire if input is valid
    * shiftTabkey: callback on shift+tab key keydown.  Will only fire if input is valid
    * onInvalid: callback when validation fails.
    * onValid: callback when validation succeeds.
    * 
    * fireValueChangedOnKeyup: trigger valueChanged on keyup
    * 
    */


    export interface TextAreaInputControlOptions {
        container: JQuery;
        validationModel: ValidationModel;

        width?: any;
        height?: any;
        readonly?: boolean;
        placeholder?: string;
        required?: boolean;
        value?: any;
        max?: any;
        min?: any;
        cssRules?: any;
        rows?: any;
        display?: any;
        validate?: any;
        change?: any;
        enterKey?: any;
        escapeKey?: any;
        tabKey?: any;
        shiftTabkey?: any;
        onInvalid?: any;
        onValid?: any;
        fireValueChangedOnKeyup?: any;
    }


    export class TextAreaInputControl {

        public _isDirty: boolean;
        public _container: JQuery;
        public _width: any;
        private _height: any;
        public _readonly: boolean;
        public _placeholder: string;
        public _required: boolean;
        public _value: any;
        public _max: any;
        public _min: any;
        public _display: any;
        public _rows: any;
        public _cssRules: any;
        public _validationModel: Solovis.UI.InputControls.ValidationModel;
        public _validate: any;
        public _change: any;
        public _enterKey: any;
        public _escapeKey: any;
        public _tabKey: any;
        public _shiftTabkey: any;
        public _onInvalid: any;
        public _onValid: any;
        public _fireValueChangedOnKeyup: any;
        public _input: JQuery;


        constructor(options: TextAreaInputControlOptions) {
            if (!options.container) {
                throw "Solovis.UI.InputControls.TextAreaInputControl - Missing required parameter: container.";
            }

            if (!options.validationModel) {
                throw "Solovis.UI.InputControls.TextAreaInputControl - Missing required parameter: validationModel.";
            }

            this._isDirty = false;

            // Base options
            this._container = options.container;
            this._width = options.width ? options.width : null;
            this._height = options.height ? options.height : null;
            this._readonly = options.readonly ? options.readonly : null;
            this._placeholder = options.placeholder ? options.placeholder : null;
            this._required = options.required ? options.required : false;
            this._value = Solovis.isNotNull(options.value) ? options.value : null;
            this._max = Solovis.isNotNull(options.max) ? options.max : null;
            this._min = Solovis.isNotNull(options.min) ? options.min : null;
            this._display = options.display ? options.display : null;
            this._rows = options.rows ? options.rows : null;
            this._cssRules = options.cssRules ? options.cssRules : null;

            this._validationModel = options.validationModel;

            // Events
            this._validate = options.validate ? options.validate : null;
            this._change = options.change ? options.change : null;
            this._enterKey = options.enterKey ? options.enterKey : null;
            this._escapeKey = options.escapeKey ? options.escapeKey : null;
            this._tabKey = options.tabKey ? options.tabKey : null;
            this._shiftTabkey = options.shiftTabkey ? options.shiftTabkey : null;
            this._onInvalid = options.onInvalid ? options.onInvalid : null;
            this._onValid = options.onValid ? options.onValid : null;

            this._fireValueChangedOnKeyup = options.fireValueChangedOnKeyup ? true : false;

            this._render();
        }


        public _render() {
            var self = this;

            this._container.addClass("solovis-input");

            if (this._container.hasClass("formbuilder-entry-container")) {
                // We specially handle the case of the TextAreaInputControl appearing directly
                // inside the form builder entry container by removing the padding. If the
                // TextAreaInputControl is nested deeper within other elements, the consumer of
                // FormBuilder and TextAreaInputControl is responsible for further styling.
                this._container
                    .css("padding-top", "0")
                    .css("padding-bottom", "0");
            }

            this._input = $("<textarea>")
                .addClass("k-textbox")
                .css("cursor", this._readonly ? "not-allowed" : null)
                .appendTo(this._container)
                .val(this._value)
                .attr("type", "text")
                .attr("readonly", this._readonly ? "" : null)
                .css("display", function () {
                    if (self._display) {
                        if (self._display.showInput === false) {
                            return "none";
                        }
                    }
                    return null;
                })
                .keyup(function (e) {
                    var key = e.keyCode || e.which;

                    if (key === 13 && self._enterKey) {
                        // Enter

                        e.preventDefault();
                        if (self.doValidate()) {
                            self._enterKey();
                        }
                    }
                    else if (key === 27 && self._escapeKey) {
                        // Escape

                        e.preventDefault();
                        self._escapeKey();
                    }

                    if (self._fireValueChangedOnKeyup) {
                        self.notifyValueChanged();
                    }
                })
                .keydown(function (e) {
                    var key = e.keyCode || e.which;

                    if (key === 9 && e.shiftKey && self._shiftTabkey) {
                        e.preventDefault();
                        // Shift Tab

                        if (self.doValidate()) {
                            self._shiftTabkey();
                        }
                    }
                    else if (key === 9 && self._tabKey) {
                        e.preventDefault();
                        // Tab

                        if (self.doValidate()) {
                            self._tabKey();
                        }
                    }
                })
                .blur(function () {
                    var val = self._input.val();
                    if (self._isDirty) {
                        self.doValidate();
                    }
                })
                .change(function () {
                    self.notifyValueChanged();
                });

            if (Solovis.isNotNull(this._width)) {
                if ($.isNumeric(this._width)) {
                    this._input.width(this._width);
                }
                else if (this._width.indexOf("%") !== -1 ||
                    this._width.indexOf("px") !== -1 ||
                    this._width.indexOf("em") !== -1) {
                    this._input.css("width", this._width);
                }
            }
            
            if (Solovis.isNotNull(this._height)) {
                if ($.isNumeric(this._height)) {
                    this._input.height(this._height);
                }
                else if (this._height.indexOf("%") !== -1 ||
                    this._height.indexOf("px") !== -1 ||
                    this._height.indexOf("em") !== -1) {
                    this._input.css("height", this._height);
                }
            }

            if (Solovis.isNotNull(this._rows)) {
                this._input.attr("rows", this._rows);
            }

            if (this._cssRules) {
                Solovis.UI.DomHelper.applyCssRules(this._input, this._cssRules);
            }
        };

        public value(value?: any): any {
            if (arguments.length) {
                this.setValue(value);
            }
            return this.getValue();
        }

        public getValue(): any {
            return this._input.val();
        }

        public setValue(value: any) {
            this._input.val(value);

            this.doValidate();

            this.notifyValueChanged();
        };

        public doValidate(): boolean {
            var isValid = this._doStandardValidation();
            if (isValid && this._validate) {
                var customValidationResults = this._validate();
                isValid = customValidationResults.isValid;
                if (!customValidationResults.isValid) {
                    this._handleInvalid(customValidationResults.message);
                }
                else {
                    this._handleValid();
                }
            }
            return isValid;
        }

        public _doStandardValidation() {
            var self = this;
            var val = this._input.val();
            var isValid = true;
            if (!val) {
                if (this._required) {
                    this._handleInvalid("This is a required field.");
                    return false;
                }
            }

            if (Solovis.isNotNull(this._min) && val.length < this._min) {
                this._handleInvalid("Field must contain " + this._min + " or more characters.");
                isValid = false;
            }
            if (Solovis.isNotNull(this._max) && val.length > this._max) {
                this._handleInvalid("Field must contain " + this._max + " or less characters.");
                isValid = false;
            }

            if (isValid) {
                this._handleValid();
            }
            return isValid;
        }

        public isValid(): boolean {
            return this.doValidate();
        }

        public notifyValueChanged() {
            $(this).trigger("valueChanged");
            if (this._change) {
                this._change();
            }
        }

        public _handleValid() {
            this._validationModel.state(null);

            if (this._onValid) {
                this._onValid();
            }
        }

        public _handleInvalid(message) {
            var self = this;
            this._validationModel.setErrorMessage(message);

            if (this._onInvalid) {
                this._onInvalid();
            }
        }
    }

}



    